import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { PASSWORD_STORAGE_KEY } from "../hooks/use-password.hook";

const AUTH_STORAGE_KEY = "driver-registrations-authenticated";

type AuthContextValue = {
    isAuthenticated: boolean;
    authenticate: (password: string) => void;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function readStoredAuth(): boolean {
    try {
        return localStorage.getItem(AUTH_STORAGE_KEY) === "true";
    } catch {
        return false;
    }
}

// Persisted in localStorage: surviving refreshes, tab closes, and browser
// restarts. Good enough for a shared gate; not a substitute for real auth.
export function AuthProvider({ children }: { children: ReactNode }) {
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(readStoredAuth);

    const authenticate = useCallback((password: string) => {
        try {
            localStorage.setItem(AUTH_STORAGE_KEY, "true");
            localStorage.setItem(PASSWORD_STORAGE_KEY, password);
        } catch {
            // localStorage unavailable (e.g. private browsing) - auth still
            // persists for the lifetime of this component tree.
        }
        setIsAuthenticated(true);
    }, []);

    const value = useMemo(() => ({ isAuthenticated, authenticate }), [isAuthenticated, authenticate]);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
}
